import { useRef, useState } from 'react';
import { toast } from 'react-toastify';

import Grid from '@material-ui/core/Grid';
import TableCell from '@material-ui/core/TableCell';
import TableRow from '@material-ui/core/TableRow';
import Typography from '@material-ui/core/Typography';

import { Button, Dialog, Table, Text } from 'components';
import { saveAs } from 'file-saver';
import { downloadXlsx } from 'helpers/downloadXLSX';
import { readXlsxFile } from 'helpers/readXLSX';
import { PageTitle } from 'pages/Layout/PageTitle';
import { providerService } from 'services';

const columns = [
  { label: 'Número OS' },
  { label: 'NF3' },
  { label: 'Data NF3' },
  { label: 'Cliente' },
  { label: 'Retorno' },
];

const extractNumeroOs = (rows) =>
  (rows || [])
    .map((row) => {
      const key = Object.keys(row).find((column) => column.trim().toLowerCase() === 'numeroos');
      return key ? String(row[key]).trim() : null;
    })
    .filter(Boolean);

export const ValidacaoIR = () => {
  const [loadingErros, setLoadingErros] = useState(false);
  const [loadingSemNf4, setLoadingSemNf4] = useState(false);
  const [loadingConsulta, setLoadingConsulta] = useState(false);
  const [loadingJsonLote, setLoadingJsonLote] = useState(false);
  const [loadingEnviarIR, setLoadingEnviarIR] = useState(false);
  const [numeroOs, setNumeroOs] = useState('');
  const [resultadoConsulta, setResultadoConsulta] = useState([]);
  const [osParaEnviar, setOsParaEnviar] = useState([]);
  const [openConfirmEnviarIR, setOpenConfirmEnviarIR] = useState(false);
  const inputJsonLoteRef = useRef(null);
  const inputEnviarIRRef = useRef(null);

  const handleDownloadErros = async () => {
    setLoadingErros(true);
    try {
      const { data } = await providerService.getControleIRErros();
      if (data?.length > 0) {
        downloadXlsx('controle_ir_erros', data);
        toast.success('Relatório de erros gerado com sucesso.');
      } else {
        toast.info('Não há erros para o período.');
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Não foi possível gerar o relatório de erros.');
    } finally {
      setLoadingErros(false);
    }
  };

  const handleDownloadSemNf4 = async () => {
    setLoadingSemNf4(true);
    try {
      const { data } = await providerService.getControleIRSemNF4();
      if (data?.length > 0) {
        downloadXlsx('controle_ir_vinculo_sem_nf4', data);
        toast.success('Relatório de vínculo sem NF4 gerado com sucesso.');
      } else {
        toast.info('Não há registros de vínculo sem NF4 para o período.');
      }
    } catch (error) {
      toast.error(
        error?.response?.data?.message || 'Não foi possível gerar o relatório de vínculo sem NF4.',
      );
    } finally {
      setLoadingSemNf4(false);
    }
  };

  const handleImportJsonLoteClick = () => {
    inputJsonLoteRef.current.value = '';
    inputJsonLoteRef.current.click();
  };

  const handleJsonLoteFileChange = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setLoadingJsonLote(true);
    try {
      const rows = await readXlsxFile(file);
      const numeroos = extractNumeroOs(rows);

      if (!numeroos.length) {
        toast.error('Não foi encontrada a coluna "NumeroOs" preenchida na planilha.');
        return;
      }

      const { data } = await providerService.getControleIRJsonLote(numeroos);
      saveAs(new Blob([data], { type: 'application/zip' }), 'controle_ir_json_lote.zip');
      toast.success('ZIP gerado com sucesso.');
    } catch (error) {
      toast.error('Não foi possível gerar o ZIP com os JSONs das OS.');
    } finally {
      setLoadingJsonLote(false);
      inputJsonLoteRef.current.value = '';
    }
  };

  const handleImportEnviarIRClick = () => {
    inputEnviarIRRef.current.value = '';
    inputEnviarIRRef.current.click();
  };

  const handleEnviarIRFileChange = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    try {
      const rows = await readXlsxFile(file);
      const numeroos = extractNumeroOs(rows);

      if (!numeroos.length) {
        toast.error('Não foi encontrada a coluna "NumeroOs" preenchida na planilha.');
        return;
      }

      setOsParaEnviar(numeroos);
      setOpenConfirmEnviarIR(true);
    } catch (error) {
      toast.error('Não foi possível ler a planilha.');
    } finally {
      inputEnviarIRRef.current.value = '';
    }
  };

  const handleCloseConfirmEnviarIR = () => {
    if (loadingEnviarIR) return;
    setOpenConfirmEnviarIR(false);
    setOsParaEnviar([]);
  };

  const handleConfirmEnviarIR = async () => {
    setLoadingEnviarIR(true);
    try {
      const { data } = await providerService.enviarControleIROSLote(osParaEnviar);
      toast.success(`${data?.total ?? osParaEnviar.length} OS enviada(s) para atualização do IR.`);
      setOpenConfirmEnviarIR(false);
      setOsParaEnviar([]);
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Não foi possível enviar as OS para o IR.');
    } finally {
      setLoadingEnviarIR(false);
    }
  };

  const handleConsultarOs = async () => {
    if (!numeroOs) {
      toast.warn('Informe o número da OS.');
      return;
    }

    setLoadingConsulta(true);
    try {
      const { data } = await providerService.getControleIRPorOS(numeroOs);
      setResultadoConsulta(data || []);
      if (!data?.length) {
        toast.info('Nenhum registro encontrado para essa OS.');
      }
    } catch (error) {
      setResultadoConsulta([]);
      toast.error(error?.response?.data?.message || 'Não foi possível consultar a OS.');
    } finally {
      setLoadingConsulta(false);
    }
  };

  return (
    <>
      <PageTitle>Validação IR</PageTitle>

      <input
        ref={inputJsonLoteRef}
        type='file'
        name='xlsx'
        onChange={handleJsonLoteFileChange}
        accept='text/xlsx'
        hidden
      />

      <input
        ref={inputEnviarIRRef}
        type='file'
        name='xlsx'
        onChange={handleEnviarIRFileChange}
        accept='text/xlsx'
        hidden
      />

      <Grid container spacing={2}>
        <Grid item xs={12} lg={3}>
          <Button fullWidth onClick={handleDownloadErros} loading={loadingErros}>
            Baixar Excel de Erros
          </Button>
        </Grid>
        <Grid item xs={12} lg={3}>
          <Button
            fullWidth
            color='secondary'
            onClick={handleDownloadSemNf4}
            loading={loadingSemNf4}
          >
            Baixar Excel Vínculo sem NF4
          </Button>
        </Grid>
        <Grid item xs={12} lg={3}>
          <Button
            fullWidth
            color='secondary'
            onClick={handleImportJsonLoteClick}
            loading={loadingJsonLote}
          >
            Importar OS (Excel) e Baixar JSON em ZIP
          </Button>
        </Grid>
        <Grid item xs={12} lg={3}>
          <Button fullWidth color='secondary' onClick={handleImportEnviarIRClick}>
            Importar OS (Excel) e Enviar para o IR
          </Button>
        </Grid>
      </Grid>

      <Grid container spacing={2} style={{ marginTop: 8 }}>
        <Grid item xs={12}>
          <Typography variant='h6'>Consultar OS</Typography>
        </Grid>
        <Grid item xs={12} md={3} lg={3}>
          <Text
            fullWidth
            size='small'
            variant='outlined'
            label='Número da OS'
            value={numeroOs}
            onChange={(event) => setNumeroOs(event.target.value)}
            loading={loadingConsulta}
          />
        </Grid>
        <Grid item xs={12} md={3} lg={3}>
          <Button fullWidth onClick={handleConsultarOs} loading={loadingConsulta}>
            Consultar
          </Button>
        </Grid>
      </Grid>

      <Grid container spacing={2} style={{ marginTop: 8 }}>
        <Grid item xs={12}>
          <Table
            name='Table'
            maxHeight
            headers={columns}
            emptyMessage='Nenhum registro encontrado.'
            disableNumeration
          >
            {resultadoConsulta?.map((item, index) => (
              <TableRow key={index}>
                <TableCell>{item?.numeroos}</TableCell>
                <TableCell>{item?.NF3}</TableCell>
                <TableCell>{item?.dtFatuNF3}</TableCell>
                <TableCell>{item?.Cliente}</TableCell>
                <TableCell>{item?.Retorno}</TableCell>
              </TableRow>
            ))}
          </Table>
        </Grid>
      </Grid>

      <Dialog open={openConfirmEnviarIR} onClose={handleCloseConfirmEnviarIR}>
        <Dialog.Title onClose={handleCloseConfirmEnviarIR}>Enviar OS para o IR</Dialog.Title>
        <Dialog.Content>
          <Typography>
            {osParaEnviar.length} OS serão colocadas na fila de atualização do IR
            (xpc_controleEnvioIR_Atualizar). Essa ação limpa a tabela auxiliar de processamento e
            não pode ser desfeita. Deseja continuar?
          </Typography>
        </Dialog.Content>
        <Dialog.Actions>
          <Button color='secondary' onClick={handleCloseConfirmEnviarIR} disabled={loadingEnviarIR}>
            Cancelar
          </Button>
          <Button onClick={handleConfirmEnviarIR} loading={loadingEnviarIR}>
            Confirmar envio
          </Button>
        </Dialog.Actions>
      </Dialog>
    </>
  );
};
